/**
 * The audit after every apply that says, from the page alone, what each card
 * became: applied, partially applied, or not applied.
 *
 * An apply path knows what it meant to write; only the page knows what stands.
 * When two cards collide, what was written for one may carry the other's words, or
 * neither's; a write can also end without an error yet leave nothing behind. So
 * once the writes are done the page is read back (`redline-landing-page-facts`)
 * and every part of every card is judged against it:
 *
 *   - a paragraph's text edit           -> the card's words stand in the paragraph's
 *                                          accepted text (`cardLandingOnParagraphText`)
 *   - a whole paragraph deleted          -> the paragraph reads empty, or is gone
 *   - a new paragraph inserted           -> its words stand in a paragraph right after
 *                                          the one it was inserted after
 *   - a table row inserted               -> a row after the anchor row holds the row's cells
 *   - a table row deleted                -> the row's bookmarked paragraph reads empty, or is gone
 *   - a table inserted                   -> a table beside the anchor holds the table's rows
 *   - a comment added                    -> a comment with its text sits on the paragraph
 *                                          or on the one after it
 *
 * The verdict is the card's status: a card whose words another card displaced, or
 * whose write left nothing on the page, is never shown as applied. The audit only
 * reads; it never changes what was written.
 */
import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { type CardLanding, cardLandingOnParagraphText, combinedCardLanding, potentialCollisionsOfCards, proximityNeedsChoice, wordsStandInText } from "@common/utils/redline/text/card-collisions";
import { ClauseMarker } from "@common/utils/redline/text/clause-marker";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import type { BookmarkedParagraphFacts, LandingPageFacts, LandingPageReader, LandingPageRequest } from "./redline-landing-page-facts";
import { logWarning } from "./sentry-reporter";

/**
 * A text card as the audit reads it.
 *
 * CASES, for what the card's paragraphs are
 *   - insertsAfterBookmarks false -> edits of the bookmarked paragraphs, each named by its bookmark;
 *                                    `card.insertions` are new paragraphs after an anchor, and
 *                                    `card.tableChanges` rows and tables
 *   - insertsAfterBookmarks true  -> new paragraphs written after the last bookmarked paragraph
 *                                    (an add-paragraph card)
 */
export interface TextCardLandingTarget {
    kind: "text_card";
    ruleId: string;
    bookmarkNames: string[];
    card: RedlineTextProposal;
    insertsAfterBookmarks: boolean;
}

/**
 * A markup card (one with no text version) as the audit reads it: the packages it
 * wrote, whose paragraphs carrying the card's bookmarks are its edits and whose
 * wholly new paragraphs are its insertions.
 */
export interface MarkupLandingTarget {
    kind: "markup";
    ruleId: string;
    bookmarkNames: string[];
    packageOoxmls: string[];
    insertsAfterBookmarks: boolean;
}

/**
 * A comment card as the audit reads it: the paragraph it belongs to and its text.
 *
 * NULL `issueCommentId`
 *   A chat comment card, which its rule id names; an Issue comment carries its own id.
 */
export interface CommentLandingTarget {
    kind: "comment";
    ruleId: string;
    bookmarkNames: string[];
    commentText: string;
    issueCommentId: string | null;
    /** The words the comment sits on, as the core placed it; null for a comment on a first word or a whole paragraph. */
    quotedText: string | null;
}

export type LandingTarget = TextCardLandingTarget | MarkupLandingTarget | CommentLandingTarget;

/**
 * One card's landing, with the sentence a card not wholly applied shows and the
 * cards whose changes collide with its own.
 *
 * EMPTY `reason` when the card is applied.
 */
export interface CardLandingVerdict {
    ruleId: string;
    landing: CardLanding;
    reason: string;
    collidingRuleIds: string[];
}

/** What a text edit shows when none of its words stand after the apply. */
export const CARD_NOT_IN_DOCUMENT_REASON = "This change is not in the document: other words stand where it would go.";

/** What a card shows when only some of its parts stand after the apply. */
export const CARD_PARTLY_IN_DOCUMENT_REASON = "Only part of this change is in the document: other words stand on the rest.";

/** What an inserted paragraph, a deleted paragraph, or a table change shows when it is not on the page. */
export const CHANGE_NOT_IN_DOCUMENT_REASON = "This change is not in the document.";

/** What a comment shows when no comment with its text sits where it was placed. */
export const COMMENT_NOT_IN_DOCUMENT_REASON = "This comment is not in the document.";

/**
 * How many paragraphs past the ones a card inserted are read as well: an insert
 * can leave an empty paragraph before or after the new ones.
 *
 * EXAMPLE
 *   a card inserting 2 paragraphs after P -> 2 + 2 = 4 paragraphs after P are read
 */
const INSERTED_PARAGRAPH_READ_SLACK = 2;

/**
 * How many paragraphs on each side of a paragraph a card edits are read, for the
 * card's text written as a paragraph of its own beside it (`editedParagraphPart`).
 *
 * EXAMPLE
 *   card on 6.2 -> the 3 paragraphs before 6.2 and the 3 after it are read
 */
const NEARBY_PARAGRAPH_WINDOW = 3;

/**
 * What one part of a card is, which picks the sentence a card not applied shows.
 *
 *   - paragraph_text -> an edit of words in an existing paragraph
 *   - page_change    -> a new or deleted paragraph, a table row, or a table
 *   - comment        -> a comment
 */
type LandingPartArea = "paragraph_text" | "page_change" | "comment";

/** One judged part of a card. */
interface LandingPart {
    area: LandingPartArea;
    landing: CardLanding;
}

/** One paragraph a card edits, named by its bookmark, with the two texts the card was written from. */
interface BookmarkedCardParagraph {
    bookmark: string;
    textWhenProposed: string;
    textProposed: string;
}

/** The paragraphs a markup card edits by bookmark, and the texts of the wholly new paragraphs it inserts. */
interface MarkupCardReading {
    editedParagraphs: BookmarkedCardParagraph[];
    insertedParagraphTexts: string[];
}

/** True when the text holds no word: an empty paragraph, or only spaces and marks. */
const holdsNoWord = (text: string): boolean => WordTokenizer.words(text).length === 0;

/**
 * Whether a paragraph's text held characters, every one of them a break or spacing:
 * a page, column or line break alone (each read as "\n"), a tab, a space.
 *
 * EXAMPLE
 *   "\n" -> true    "" -> false    "CHAPTER 15" -> false
 */
const heldOnlyBreaksOrSpacing = (text: string): boolean => text.length > 0 && text.trim() === "";

/**
 * True when the card changes a word of the paragraph. A paragraph a card carries
 * along unchanged (the anchor of an insertion, a neighbour in its range) asks the
 * audit nothing, so it never counts as a part that landed.
 */
const changesAnyWord = (edited: BookmarkedCardParagraph): boolean =>
    WordTokenizer.words(edited.textWhenProposed).join(" ") !== WordTokenizer.words(edited.textProposed).join(" ");

/**
 * True when the card asks the audit about the paragraph: it changes a word of it, or it
 * deletes a paragraph that held only a break or spacing (a page break alone between
 * chapters), which changes no word and is still a paragraph the card takes out.
 *
 * EXAMPLE
 *   "\n" -> ""                    -> true: the spacer is deleted
 *   "\n" -> "\n"                  -> false: carried along
 *   "Pay in 30 days." -> unchanged -> false
 */
const changesTheParagraph = (edited: BookmarkedCardParagraph): boolean =>
    changesAnyWord(edited) || (heldOnlyBreaksOrSpacing(edited.textWhenProposed) && edited.textProposed === "");

/**
 * The paragraphs of a markup card's packages, split into the edits of the
 * bookmarked paragraphs and the new paragraphs.
 *
 * CASES, per package paragraph
 *   - carries one of the card's bookmarks      -> an edit: its all-rejected text is what the card
 *                                                 saw, its all-accepted text what it wants
 *   - an add-paragraph card's paragraph         -> new, when it has words
 *   - no card bookmark, nothing to reject, and
 *     words once accepted                       -> new: a wholly inserted paragraph
 *   - anything else                             -> an untouched neighbour the package carries along
 *
 * EDGE CASE
 *   A bookmark two package paragraphs carry is read from the first.
 */
function markupCardReadingOf(target: MarkupLandingTarget): MarkupCardReading {
    const editedParagraphs: BookmarkedCardParagraph[] = [];
    const insertedParagraphTexts: string[] = [];
    for (const packageOoxml of target.packageOoxmls) {
        const packageParagraphs = new OoxmlRange(packageOoxml).documentParagraphs(DocumentComments.none(), OoxmlNumbering.none(), OoxmlOutlineLevels.none());
        for (const packageParagraph of packageParagraphs) {
            const cardBookmark = packageParagraph.bookmarkNames.find((bookmarkName) => target.bookmarkNames.includes(bookmarkName));
            if (!target.insertsAfterBookmarks && cardBookmark !== undefined) {
                if (!editedParagraphs.some((edited) => edited.bookmark === cardBookmark)) {
                    editedParagraphs.push({ bookmark: cardBookmark, textWhenProposed: packageParagraph.allChangesRejectedText, textProposed: packageParagraph.allChangesAcceptedText });
                }
                continue;
            }
            const isWhollyNew = target.insertsAfterBookmarks || holdsNoWord(packageParagraph.allChangesRejectedText);
            if (isWhollyNew && !holdsNoWord(packageParagraph.allChangesAcceptedText)) insertedParagraphTexts.push(packageParagraph.allChangesAcceptedText);
        }
    }
    return { editedParagraphs: editedParagraphs.filter(changesAnyWord), insertedParagraphTexts };
}

/**
 * The paragraphs a text card edits, by bookmark.
 *
 * EMPTY for an add-paragraph card, whose paragraphs are all new.
 */
function editedParagraphsOfTextCard(target: TextCardLandingTarget): BookmarkedCardParagraph[] {
    if (target.insertsAfterBookmarks) return [];
    return target.card.paragraphs
        .flatMap((cardParagraph) => (cardParagraph.bookmark ? [{ bookmark: cardParagraph.bookmark, textWhenProposed: cardParagraph.textWhenProposed, textProposed: cardParagraph.textProposed }] : []))
        .filter(changesTheParagraph);
}

/** A request for one bookmark, merged as more cards ask about it; `readsNearbyParagraphs` is set by a card that edits the bookmarked paragraph's words. */
interface MergedRequestParts {
    insertedParagraphCount: number;
    readsComments: boolean;
    readsTables: boolean;
    readsNearbyParagraphs: boolean;
}

/**
 * What the page is asked, one request per bookmark any card names, each asking for
 * everything any card needs of that bookmark.
 *
 * EXAMPLE
 *   card a inserts 1 paragraph after P, card b inserts 2 after P, card c comments on P
 *   -> { bookmark P, followingParagraphCount: 3 + 2 = 5, readsComments: true, readsTables: false }
 */
function landingRequestsOf(targets: LandingTarget[]): LandingPageRequest[] {
    const partsByBookmark = new Map<string, MergedRequestParts>();
    const partsOf = (bookmark: string): MergedRequestParts => {
        const existing = partsByBookmark.get(bookmark);
        if (existing !== undefined) return existing;
        const created = { insertedParagraphCount: 0, readsComments: false, readsTables: false, readsNearbyParagraphs: false };
        partsByBookmark.set(bookmark, created);
        return created;
    };
    /** Every bookmark an insertion was written after is asked for as many following paragraphs as land there. */
    const askAfterAnchors = (anchorBookmarks: string[], insertedCount: number): void => {
        anchorBookmarks.forEach((anchorBookmark) => { partsOf(anchorBookmark).insertedParagraphCount += insertedCount; });
    };
    for (const target of targets) {
        if (target.kind === "comment") {
            target.bookmarkNames.forEach((bookmark) => { partsOf(bookmark).readsComments = true; });
            continue;
        }
        if (target.kind === "markup") {
            const reading = markupCardReadingOf(target);
            reading.editedParagraphs.forEach((edited) => partsOf(edited.bookmark));
            if (reading.insertedParagraphTexts.length > 0) askAfterAnchors(target.bookmarkNames, reading.insertedParagraphTexts.length);
            continue;
        }
        editedParagraphsOfTextCard(target).forEach((edited) => { partsOf(edited.bookmark).readsNearbyParagraphs = true; });
        if (target.insertsAfterBookmarks) askAfterAnchors(target.bookmarkNames, target.card.paragraphs.length);
        target.card.insertions.forEach((insertion) => askAfterAnchors([insertion.afterBookmark], 1));
        target.card.tableChanges.rowInsertions.forEach((rowInsertion) => { partsOf(rowInsertion.afterRowOfBookmark).readsTables = true; });
        target.card.tableChanges.rowDeletions.forEach((rowDeletion) => partsOf(rowDeletion.rowOfBookmark));
        /**
         * A table inserted after a paragraph is asked for one following paragraph as well, its
         * first cell: the reader reads on past another card's new paragraphs to the table.
         */
        target.card.tableChanges.tableInsertions.forEach((tableInsertion) => {
            partsOf(tableInsertion.afterBookmark).readsTables = true;
            askAfterAnchors([tableInsertion.afterBookmark], 1);
        });
    }
    return [...partsByBookmark.entries()].map(([bookmark, parts]) => ({
        bookmark,
        /**
         * CASES
         *   - paragraphs were inserted after it -> those, plus the slack for an empty paragraph beside them
         *   - a comment asks about it           -> at least 1: an "after_paragraph" comment sits on the next one
         *   - neither                           -> 0
         */
        followingParagraphCount: Math.max(parts.insertedParagraphCount > 0 ? parts.insertedParagraphCount + INSERTED_PARAGRAPH_READ_SLACK : 0, parts.readsComments ? 1 : 0),
        nearbyParagraphCount: parts.readsNearbyParagraphs ? NEARBY_PARAGRAPH_WINDOW : 0,
        readsComments: parts.readsComments,
        readsTables: parts.readsTables,
    }));
}

/**
 * The paragraph's text with its typed clause number set aside (`ClauseMarker`).
 *
 * WHY
 *   The redline engine never strikes a typed clause number: a card that deletes a
 *   whole clause leaves "1.7 " standing, which is the deletion done as the engine
 *   writes it. The marker is looked for with one space after the trimmed text, since
 *   a paragraph left holding only "1.7" has no trailing space for the marker to end on.
 *
 * EXAMPLE
 *   "1.7 "                                  -> ""
 *   "1.7 This Agreement may be executed"    -> "This Agreement may be executed"
 */
function withoutLeadingClauseMarker(acceptedText: string): string {
    const trimmedText = acceptedText.trim();
    const clauseMarker = ClauseMarker.leading(`${trimmedText} `);
    /** `clauseMarker.length` counts the marker and its one space, so the slice starts at the first word after the number. */
    return clauseMarker === null ? acceptedText : `${trimmedText} `.slice(clauseMarker.length);
}

/**
 * The landing of one edited paragraph.
 *
 * CASES
 *   - the card empties the paragraph (a whole-paragraph deletion)
 *       - the paragraph is gone, reads empty, or holds
 *         only its typed clause number                  -> applied
 *       - it still has words                            -> not_applied
 *   - the card changes words
 *       - the paragraph is gone                         -> not_applied
 *       - its text stands in the paragraph              -> applied
 *       - its text stands across every paragraph the
 *         bookmark spans                                -> applied
 *       - its text stands in a paragraph the apply
 *         wrote within `NEARBY_PARAGRAPH_WINDOW` of it   -> applied
 *       - otherwise                                     -> `cardLandingOnParagraphText` of the paragraph
 *
 * WHY a written paragraph nearby
 *   The apply writes the card's text where Word lets it, not always into the
 *   bookmarked paragraph: over a clause the user deleted as a tracked change, the
 *   card's text lands as a new paragraph beside the struck one, which keeps the
 *   bookmark and reads empty (6.2). Read on the bookmark alone, Word held the card
 *   and the card read "Failed".
 *
 * WHY every paragraph the bookmark spans
 *   A user who split the paragraph by hand left the bookmark across both halves, and
 *   a card proposed before the split changes words of either (R22, 7.4). Only the
 *   text edit reads the span: a whole-paragraph deletion stays judged on the
 *   bookmarked paragraph, as before.
 */
function editedParagraphPart(edited: BookmarkedCardParagraph, facts: LandingPageFacts): LandingPart {
    const paragraphFacts = facts.get(edited.bookmark);
    if (holdsNoWord(edited.textProposed)) {
        /**
         * CASES, for a paragraph the card empties
         *   - it is gone                                         -> emptied
         *   - it held only a break or spacing when proposed (a
         *     page break alone between chapters)                 -> emptied only once nothing of it is
         *                                                           left: it holds no word either way
         *   - any other paragraph                                -> emptied once it holds no word
         *
         * WHY the spacer is read by its characters
         *   Read by its words, a spacer reads empty whether or not its break was struck: on
         *   Word for Mac the card that deleted a chapter left its page-break paragraph whole
         *   and still read Applied (R34 item 4, -ba's R35 step 3b, 2026-10-16).
         */
        const paragraphIsEmptied = paragraphFacts === undefined || (heldOnlyBreaksOrSpacing(edited.textWhenProposed)
            ? paragraphFacts.acceptedText === ""
            : holdsNoWord(withoutLeadingClauseMarker(paragraphFacts.acceptedText)));
        return { area: "page_change", landing: paragraphIsEmptied ? "applied" : "not_applied" };
    }
    if (paragraphFacts === undefined) return { area: "paragraph_text", landing: "not_applied" };
    const landingInParagraph = cardLandingOnParagraphText(edited, paragraphFacts.acceptedText);
    if (landingInParagraph === "applied") return { area: "paragraph_text", landing: "applied" };
    /** WHEN the bookmark runs on past its first paragraph CONSEQUENCE the card is judged once more on every paragraph it spans. */
    const landsAcrossBookmarkSpan = paragraphFacts.spannedAcceptedText !== paragraphFacts.acceptedText && cardLandingOnParagraphText(edited, paragraphFacts.spannedAcceptedText) === "applied";
    const landsInWrittenParagraphNearby = paragraphFacts.writtenNearbyAcceptedTexts.some((writtenText) => cardLandingOnParagraphText(edited, writtenText) === "applied");
    return { area: "paragraph_text", landing: landsAcrossBookmarkSpan || landsInWrittenParagraphNearby ? "applied" : landingInParagraph };
}

/**
 * The landing of one new paragraph: its words stand in a paragraph right after
 * any of the paragraphs it was inserted after.
 */
function insertedParagraphPart(insertedText: string, anchorBookmarks: string[], facts: LandingPageFacts): LandingPart {
    const standsAfterAnchor = anchorBookmarks.some((anchorBookmark) =>
        (facts.get(anchorBookmark)?.followingAcceptedTexts ?? []).some((followingText) => wordsStandInText(insertedText, followingText)));
    return { area: "page_change", landing: standsAfterAnchor ? "applied" : "not_applied" };
}

/**
 * True when a table row holds every cell text a card asked for, in order, each in
 * a cell of its own. A merged cell or an empty asked-for cell shifts nothing: only
 * the asked-for texts with words are looked for.
 *
 * EXAMPLE
 *   asked ["Premium onboarding", "", "2,500"]   row ["Premium onboarding", "", "2,500"] -> true
 *   asked ["Premium onboarding", "2,500"]       row ["2,500", "Premium onboarding"]     -> false: out of order
 */
function rowHoldsCellTexts(rowCellTexts: string[], askedCellTexts: string[]): boolean {
    let nextCellIndex = 0;
    for (const askedCellText of askedCellTexts.filter((cellText) => !holdsNoWord(cellText))) {
        /** The search starts at the cell after the last match, so the asked texts must stand left to right. */
        while (nextCellIndex < rowCellTexts.length && !wordsStandInText(askedCellText, rowCellTexts[nextCellIndex])) nextCellIndex += 1;
        if (nextCellIndex === rowCellTexts.length) return false;
        nextCellIndex += 1;
    }
    return true;
}

/**
 * True when a table holds exactly the rows a card asked a new table to have: the
 * same number of rows, each holding its asked-for cells.
 */
const tableHoldsRows = (tableCellTexts: string[][], askedRows: string[][]): boolean =>
    tableCellTexts.length === askedRows.length && askedRows.every((askedRow, rowIndex) => rowHoldsCellTexts(tableCellTexts[rowIndex], askedRow));

/**
 * The landing of one inserted table row: a row after the anchor row holds it.
 *
 * EXAMPLE
 *   anchorRowIndex 1, rowCellTexts [header, anchor, new row, total]
 *   -> slice(1 + 1) = [new row, total]: the rows after the anchor row, where Word inserted it
 */
function insertedRowPart(anchorFacts: BookmarkedParagraphFacts | undefined, askedRow: string[]): LandingPart {
    const enclosingTable = anchorFacts?.enclosingTable ?? null;
    const rowsAfterAnchor = enclosingTable === null ? [] : enclosingTable.rowCellTexts.slice(enclosingTable.anchorRowIndex + 1);
    return { area: "page_change", landing: rowsAfterAnchor.some((rowCellTexts) => rowHoldsCellTexts(rowCellTexts, askedRow)) ? "applied" : "not_applied" };
}

/**
 * Every judged part of a text card.
 *
 * CASES, for an add-paragraph card
 *   - each of its paragraphs with words -> stands after its bookmarks; an empty one asks for nothing
 */
function textCardParts(target: TextCardLandingTarget, facts: LandingPageFacts): LandingPart[] {
    const { tableChanges, insertions, paragraphs } = target.card;
    const addedParagraphParts = target.insertsAfterBookmarks
        ? paragraphs.filter((cardParagraph) => !holdsNoWord(cardParagraph.textProposed)).map((cardParagraph) => insertedParagraphPart(cardParagraph.textProposed, target.bookmarkNames, facts))
        : [];
    return [
        ...editedParagraphsOfTextCard(target).map((edited) => editedParagraphPart(edited, facts)),
        ...addedParagraphParts,
        ...insertions.filter((insertion) => !holdsNoWord(insertion.textProposed)).map((insertion) => insertedParagraphPart(insertion.textProposed, [insertion.afterBookmark], facts)),
        ...tableChanges.rowInsertions.flatMap((rowInsertion) => rowInsertion.rows.map((askedRow) => insertedRowPart(facts.get(rowInsertion.afterRowOfBookmark), askedRow))),
        ...tableChanges.rowDeletions.map((rowDeletion) => deletedRowPart(facts.get(rowDeletion.rowOfBookmark))),
        ...tableChanges.tableInsertions.map((tableInsertion) => insertedTablePart(facts.get(tableInsertion.afterBookmark), tableInsertion.rows)),
    ];
}

/**
 * The landing of one deleted table row, read off its bookmarked paragraph.
 *
 * CASES
 *   - the paragraph is gone, or reads empty once the tracked deletion is accepted -> applied
 *   - it still has words: the row stands                                          -> not_applied
 */
function deletedRowPart(rowFacts: BookmarkedParagraphFacts | undefined): LandingPart {
    const rowIsDeleted = rowFacts === undefined || holdsNoWord(rowFacts.acceptedText);
    return { area: "page_change", landing: rowIsDeleted ? "applied" : "not_applied" };
}

/** The landing of one inserted table: a table beside the anchor (after it, nested in its cell, or after its table) holds the asked-for rows. */
function insertedTablePart(anchorFacts: BookmarkedParagraphFacts | undefined, askedRows: string[][]): LandingPart {
    const nearbyTables = anchorFacts?.nearbyTablesCellTexts ?? [];
    return { area: "page_change", landing: nearbyTables.some((tableCellTexts) => tableHoldsRows(tableCellTexts, askedRows)) ? "applied" : "not_applied" };
}

/** Every judged part of one card, by what kind of card it is. */
function partsOfTarget(target: LandingTarget, facts: LandingPageFacts): LandingPart[] {
    if (target.kind === "comment") return commentParts(target, facts);
    if (target.kind === "markup") return markupCardParts(target, facts);
    return textCardParts(target, facts);
}

/** Every judged part of a markup card: its edited paragraphs, and its new paragraphs after its bookmarks. */
function markupCardParts(target: MarkupLandingTarget, facts: LandingPageFacts): LandingPart[] {
    const reading = markupCardReadingOf(target);
    return [
        ...reading.editedParagraphs.map((edited) => editedParagraphPart(edited, facts)),
        ...reading.insertedParagraphTexts.map((insertedText) => insertedParagraphPart(insertedText, target.bookmarkNames, facts)),
    ];
}

/**
 * The one part of a comment card: a comment whose words are the card's, no more
 * and no fewer, sits on the paragraph or on the one after it.
 */
function commentParts(target: CommentLandingTarget, facts: LandingPageFacts): LandingPart[] {
    const wantedWords = WordTokenizer.words(target.commentText).join(" ");
    const commentStands = target.bookmarkNames.some((bookmark) =>
        (facts.get(bookmark)?.commentTexts ?? []).some((commentText) => WordTokenizer.words(commentText).join(" ") === wantedWords));
    return [{ area: "comment", landing: commentStands ? "applied" : "not_applied" }];
}

/**
 * The sentence a card shows for its landing.
 *
 * CASES
 *   - applied                                  -> ""
 *   - partial                                  -> CARD_PARTLY_IN_DOCUMENT_REASON
 *   - not applied, a comment                   -> COMMENT_NOT_IN_DOCUMENT_REASON
 *   - not applied, a text edit failed          -> CARD_NOT_IN_DOCUMENT_REASON: other words stand there
 *   - not applied, only page changes failed    -> CHANGE_NOT_IN_DOCUMENT_REASON
 */
function reasonOf(landing: CardLanding, parts: LandingPart[]): string {
    if (landing === "applied") return "";
    if (landing === "partial") return CARD_PARTLY_IN_DOCUMENT_REASON;
    if (parts.some((part) => part.area === "comment")) return COMMENT_NOT_IN_DOCUMENT_REASON;
    return parts.some((part) => part.area === "paragraph_text" && part.landing !== "applied") ? CARD_NOT_IN_DOCUMENT_REASON : CHANGE_NOT_IN_DOCUMENT_REASON;
}

/**
 * For each card, the cards it conflicts with on a paragraph both edit
 * (`proximityNeedsChoice`: the relations Apply All chooses between): the cards a
 * card not applied lost to. A card that made the same change, edited nearby or
 * only shares the sentence never displaces another; a comment collides with nothing.
 */
function collidingRuleIdsByCard(targets: LandingTarget[]): Map<string, string[]> {
    const collidingRuleIdsOf = new Map<string, string[]>();
    const collisionCards = targets.flatMap((target) => {
        if (target.kind === "comment") return [];
        const editedParagraphs = target.kind === "markup" ? markupCardReadingOf(target).editedParagraphs : editedParagraphsOfTextCard(target);
        return [{ cardId: target.ruleId, paragraphs: editedParagraphs }];
    });
    for (const collision of potentialCollisionsOfCards(collisionCards)) {
        if (!proximityNeedsChoice(collision.proximity)) continue;
        collidingRuleIdsOf.set(collision.cardId, [...(collidingRuleIdsOf.get(collision.cardId) ?? []), collision.otherCardId]);
    }
    return collidingRuleIdsOf;
}

/**
 * Every card's landing on the page as Word reads it now, the page read once for
 * all of them.
 *
 * CASES, per card, over its judged parts
 *   - every part stands                        -> applied
 *   - none does                                -> not_applied
 *   - some do                                  -> partial
 *   - the card asked for nothing to judge      -> applied
 *
 * EXAMPLE
 *   cards 45 and 90 on PAY; the page reads forty-five
 *   -> [{ 45, applied }, { 90, not_applied, CARD_NOT_IN_DOCUMENT_REASON, colliding [45] }]
 */
export async function auditCardLandings(targets: LandingTarget[], readPage: LandingPageReader): Promise<CardLandingVerdict[]> {
    /** A run with nothing to judge reads nothing from the page. */
    if (targets.length === 0) return [];
    const facts = await readPage(landingRequestsOf(targets));
    const collidingRuleIdsOf = collidingRuleIdsByCard(targets);
    const verdicts = targets.map((target) => {
        const parts = partsOfTarget(target, facts);
        const landing = combinedCardLanding(parts.map((part) => part.landing));
        return { ruleId: target.ruleId, landing, reason: reasonOf(landing, parts), collidingRuleIds: collidingRuleIdsOf.get(target.ruleId) ?? [] };
    });
    return verdicts;
}

/** The message a landing mismatch is logged under, so every one reads as one kind of event. */
export const LANDING_MISMATCH_LOG_MESSAGE = "landing audit: a written card reads differently from what was written";

/**
 * Logs every written card and comment the landing audit reads differently from what
 * was written, as telemetry: the card's id (and an Issue comment's own id), how it
 * read, the audit's reason, the cards it collides with and its bookmarks.
 *
 * WHY telemetry only
 *   No read after a write changes a card's state or its posted outcome: the outcome
 *   comes from what the write itself returned, and a card's chosen or superseded state
 *   from the run's own decisions (the zone choices and the composition the engine made
 *   before writing). Word for Mac's tracked-change reads lag a fresh write (-26's live
 *   run, 2026-10-18), so a read that grades the card mislabels writes that fully landed.
 *   Within one run the engine composed every write, so one card's write standing over
 *   another's is a defect or a Word surprise: logged, never a label (the lead's A22
 *   rule, 2026-10-18). A comment's outcome likewise comes from what the comment writer
 *   returned.
 *
 * CASES, per verdict and the target it judged (one per index)
 *   - it reads as written                      -> nothing logged
 *   - a card or comment that reads differently -> one log line, an Issue comment's id with it
 *
 * EXAMPLE
 *   verdicts [payment applied, severity not_applied "Its words are not in the document."]
 *   -> one line for severity, landing not_applied, its bookmarks
 */
export function logLandingMismatches(verdicts: CardLandingVerdict[], judgedTargets: LandingTarget[]): void {
    verdicts.forEach((verdict, judgedTargetIndex) => {
        const judgedTarget = judgedTargets[judgedTargetIndex];
        /** WHEN it reads as written CONSEQUENCE nothing is logged for it. */
        if (verdict.landing === "applied" || judgedTarget === undefined) return;
        /** CASES an Issue comment -> its own id rides along; anything else -> none */
        const issueCommentAttributes = judgedTarget.kind === "comment" && judgedTarget.issueCommentId !== null ? { issueCommentId: judgedTarget.issueCommentId } : {};
        logWarning(LANDING_MISMATCH_LOG_MESSAGE, {
            ruleId: verdict.ruleId,
            targetKind: judgedTarget.kind,
            landing: verdict.landing,
            reason: verdict.reason,
            collidingRuleIds: verdict.collidingRuleIds,
            bookmarkNames: judgedTarget.bookmarkNames,
            ...issueCommentAttributes,
        });
    });
}
